import { useMemo } from "react";

import { typedQueryClient } from "hooks/useTypedQuery";
import { LocationLicenseOverviewKeys } from "modules/LocationLicenseOverview/queryKeys";
import { updateCategoryArray } from "modules/LocationLicenseOverview/utils";

import type { WebSocketMessageListener } from "components";
import type { DealerLocation, ExactCategory, ExactStandaloneLicense } from "models";

type LocationLicenseOverviewListenersProps = {
  selectedLocation?: DealerLocation | null;
};

export const useLocationLicenseOverviewListeners = ({ selectedLocation }: LocationLicenseOverviewListenersProps) => {
  const listeners = useMemo(() => {
    if (!selectedLocation?.id) return [];

    const exactStandaloneLicenseListeners: WebSocketMessageListener<ExactStandaloneLicense>[] = [
      {
        model: "ExactStandaloneLicense",
        action: "upsert",
        callback: ({ data: license }) => {
          const licenses = typedQueryClient.getQueryData(LocationLicenseOverviewKeys.licenses);
          if (!licenses) return;

          const existingLicense = licenses.standalone_licenses?.some(lc => lc.id === license.id);

          typedQueryClient.setQueryData(LocationLicenseOverviewKeys.licenses, {
            ...licenses,
            standalone_licenses: existingLicense
              ? licenses.standalone_licenses?.map(lc => (lc.id === license.id ? license : lc))
              : [...(licenses.standalone_licenses || []), license]
          });
        }
      },
      {
        model: "ExactStandaloneLicense",
        action: "delete",
        callback: ({ data: license }) => {
          const licenses = typedQueryClient.getQueryData(LocationLicenseOverviewKeys.licenses);
          if (!licenses) return;

          typedQueryClient.setQueryData(LocationLicenseOverviewKeys.licenses, {
            ...licenses,
            standalone_licenses: licenses.standalone_licenses?.filter(lc => lc.id !== license.id)
          });
        }
      }
    ];

    const exactCategoryListeners: WebSocketMessageListener<ExactCategory>[] = [
      {
        model: "ExactCategory",
        action: "upsert",
        callback: ({ data: category }) => {
          const licenses = typedQueryClient.getQueryData(LocationLicenseOverviewKeys.licenses);
          if (!licenses) return;

          typedQueryClient.setQueryData(LocationLicenseOverviewKeys.licenses, {
            ...licenses,
            categories: updateCategoryArray(licenses.categories ?? [], category)
          });
        }
      }
    ];

    return [...exactStandaloneLicenseListeners, ...exactCategoryListeners];
  }, [selectedLocation?.id]);

  return { listeners };
};
